import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatFullAddress } from '@occc/utils-formatters';
import { Box, CircularProgress, SxProps } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import {
  EmptyDataTable,
  LinearProgress,
  NO_DATA_PLACEHOLDER,
  Pagination,
  SearchForm,
  SearchFormProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableRowWithLink,
  IconButton,
  MutedContent,
} from '@occc-app/design-system';
import { useGetSiteManagementLocations } from '@occc-app/dataAccessLayer/location/queries';
import { PaginationInfo } from '@occc-app/hooks/usePaginatedQuery';
import { routeCreator as locationRouteCreator } from '@occc-app/routes/Location/utils';
import type { SiteManagementLocationFragment } from '@occc/graphql';

export const STORES_TABLE_PAGE_SIZE = 10;

const getStoreIdsKey = (storeIds: string[]) => [...storeIds].sort().join(',');

type StoresTableProps = {
  storeIds: string[];
  title: string;
  onClose?: () => void;
  tableSx?: SxProps;
};

export const StoresTable = ({ storeIds, title, onClose, tableSx }: StoresTableProps) => {
  const [search, setSearch] = useState('');
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo>({});

  const storeIdsKey = getStoreIdsKey(storeIds);
  const stableStoreIds = useMemo(() => (storeIdsKey ? storeIdsKey.split(',') : []), [storeIdsKey]);

  useEffect(() => {
    setSearch('');
    setPaginationInfo({});
  }, [storeIdsKey]);

  const filter = useMemo(
    () => ({
      uuidContains: stableStoreIds,
      search: search.trim() || undefined,
    }),
    [stableStoreIds, search],
  );

  const {
    data: locations,
    loading,
    error,
    paginationProps,
  } = useGetSiteManagementLocations({
    filter,
    pageSize: STORES_TABLE_PAGE_SIZE,
    paginationInfo,
    onPageChange: setPaginationInfo,
    baseOptions: {
      skip: storeIds.length === 0,
    },
  });

  const handleSearchSubmit = useCallback<SearchFormProps['onSubmit']>(({ search }) => {
    setSearch(search ?? '');
    setPaginationInfo({});
  }, []);

  const noData = <MutedContent>{NO_DATA_PLACEHOLDER}</MutedContent>;
  const isInitialLoading = loading && !locations;

  if (storeIds.length === 0) {
    return (
      <TableContainer elevation={0} square>
        <TableHeader title={title} />
        <Box sx={tableSx}>
          <Table>
            <TableBody>
              <TableRow hover={false}>
                <TableCell colSpan={4} align='center'>
                  No stores
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </TableContainer>
    );
  }

  if (isInitialLoading) {
    return (
      <Box display='flex' alignItems='center' justifyContent='center' width='30vw' sx={tableSx}>
        <CircularProgress size={24} color='primary' />
      </Box>
    );
  }

  const emptyMessage = search.trim() ? 'No matching stores' : 'No stores';

  return (
    <TableContainer elevation={0} square>
      <TableHeader
        title={title}
        actions={
          <Box display='flex' alignItems='center' gap={1}>
            <SearchForm onSubmit={handleSearchSubmit} initialValues={{ search }} />
            {onClose && (
              <IconButton onClick={onClose} size='medium' aria-label='Close dialog'>
                <CloseIcon fontSize='inherit' />
              </IconButton>
            )}
          </Box>
        }
      />
      {error ? (
        <Box sx={tableSx}>
          <EmptyDataTable />
        </Box>
      ) : (
        <Box sx={tableSx}>
          {loading && <LinearProgress />}
          <Table>
            <TableHead>
              <TableRow hover={false}>
                <TableCell>Site ID</TableCell>
                <TableCell>Site Name (DBA)</TableCell>
                <TableCell>Site Address</TableCell>
                <TableCell align='right'>Site Phone Number</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!locations?.length ? (
                <TableRow hover={false}>
                  <TableCell colSpan={4} align='center'>
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                locations.map((location: SiteManagementLocationFragment) => (
                  <TableRowWithLink
                    key={location.uuid}
                    to={locationRouteCreator.generalDetails(location.uuid)}
                  >
                    <TableCell>
                      {location.siteSystemConfiguration?.length
                        ? location.siteSystemConfiguration.map((v) => v.identifier).join(', ')
                        : noData}
                    </TableCell>
                    <TableCell>{location.name ?? noData}</TableCell>
                    <TableCell>{formatFullAddress(location.address) || noData}</TableCell>
                    <TableCell align='right'>
                      {location.phoneNumbers?.length
                        ? location.phoneNumbers.map((v) => v.phoneNumber).join(', ')
                        : noData}
                    </TableCell>
                  </TableRowWithLink>
                ))
              )}
            </TableBody>
          </Table>
          {!!locations?.length && <Pagination {...paginationProps} />}
        </Box>
      )}
    </TableContainer>
  );
};
